/**
 * 场景序列化器
 *
 * 负责整个场景的序列化和反序列化，包括实体、组件等
 */
import { ComponentRegistry } from '../Core/ComponentStorage';
import { EntitySerializer } from './EntitySerializer';
import { getComponentTypeName } from '../Decorators';
import { getSerializationMetadata } from './SerializationDecorators';
import { BinarySerializer } from '../../Utils/BinarySerializer';
/**
 * 场景序列化器类
 */
export class SceneSerializer {
    /**
     * 序列化场景
     *
     * @param scene 要序列化的场景
     * @param options 序列化选项
     * @returns 序列化后的数据（JSON字符串或二进制Uint8Array）
     */
    static serialize(scene, options) {
        const opts = {
            systems: false,
            format: 'json',
            pretty: true,
            includeMetadata: true,
            ...options
        };
        // 过滤实体和组件
        const entities = this.filterEntities(scene, opts);
        // 序列化实体
        const serializedEntities = EntitySerializer.serializeEntities(entities, true);
        // 收集组件类型信息
        const componentTypeRegistry = this.buildComponentTypeRegistry(entities);
        // 序列化场景自定义数据
        const sceneData = this.serializeSceneData(scene.sceneData);
        // 构建序列化数据
        const serializedScene = {
            name: scene.name,
            version: this.SERIALIZATION_VERSION,
            entities: serializedEntities,
            componentTypeRegistry
        };
        // 添加场景数据（如果有）
        if (sceneData && Object.keys(sceneData).length > 0) {
            serializedScene.sceneData = sceneData;
        }
        // 添加元数据
        if (opts.includeMetadata) {
            serializedScene.timestamp = Date.now();
            serializedScene.metadata = {
                entityCount: serializedEntities.length,
                componentTypeCount: componentTypeRegistry.length,
                serializationOptions: opts
            };
        }
        if (opts.format === 'json') {
            return opts.pretty
                ? JSON.stringify(serializedScene, null, 2)
                : JSON.stringify(serializedScene);
        }
        else {
            return BinarySerializer.encode(serializedScene);
        }
    }
    /**
     * 反序列化场景
     *
     * @param scene 目标场景
     * @param saveData 序列化的数据（JSON字符串或二进制Uint8Array）
     * @param options 反序列化选项
     */
    static deserialize(scene, saveData, options) {
        const opts = {
            strategy: 'replace',
            preserveIds: false,
            ...options
        };
        let serializedScene;
        try {
            if (typeof saveData === 'string') {
                serializedScene = JSON.parse(saveData);
            }
            else {
                serializedScene = BinarySerializer.decode(saveData);
            }
        }
        catch (error) {
            throw new Error(`Failed to parse save data: ${error}`);
        }
        // 版本迁移
        if (opts.migration && serializedScene.version !== this.SERIALIZATION_VERSION) {
            serializedScene = opts.migration(serializedScene.version, this.SERIALIZATION_VERSION, serializedScene);
        }
        // 构建组件注册表
        const componentRegistry = opts.componentRegistry || this.getGlobalComponentRegistry();
        // 根据策略处理场景
        if (opts.strategy === 'replace') {
            // 清空场景
            scene.destroyAllEntities();
        }
        // ID生成器
        const idGenerator = () => scene.identifierPool.checkOut();
        // 反序列化实体
        const entities = EntitySerializer.deserializeEntities(serializedScene.entities, componentRegistry, idGenerator, opts.preserveIds || false, scene);
        // 将实体添加到场景
        for (const entity of entities) {
            scene.addEntity(entity, true);
            this.addChildrenRecursively(entity, scene);
        }
        // 统一清理缓存（批量操作完成后）
        scene.querySystem.clearCache();
        scene.clearSystemEntityCaches();
        // 反序列化场景自定义数据
        if (serializedScene.sceneData) {
            this.deserializeSceneData(serializedScene.sceneData, scene.sceneData);
        }
    }
    /**
     * 递归添加实体的所有子实体到场景
     *
     * 修复反序列化时子实体丢失的问题：
     * EntitySerializer.deserialize会提前设置子实体的scene引用，
     * 导致Entity.addChild的条件判断(!child.scene)跳过scene.addEntity调用。
     * 因此需要在SceneSerializer中统一递归添加所有子实体。
     *
     * @param entity 父实体
     * @param scene 目标场景
     */
    static addChildrenRecursively(entity, scene) {
        for (const child of entity.children) {
            scene.addEntity(child, true); // 延迟缓存清理
            this.addChildrenRecursively(child, scene); // 递归处理子实体的子实体
        }
    }
    /**
     * 序列化场景自定义数据
     *
     * 将 Map<string, any> 转换为普通对象
     */
    static serializeSceneData(sceneData) {
        const result = {};
        for (const [key, value] of sceneData) {
            result[key] = this.serializeValue(value);
        }
        return result;
    }
    /**
     * 反序列化场景自定义数据
     *
     * 将普通对象还原为 Map<string, any>
     */
    static deserializeSceneData(data, targetMap) {
        targetMap.clear();
        for (const [key, value] of Object.entries(data)) {
            targetMap.set(key, this.deserializeValue(value));
        }
    }
    /**
     * 序列化单个值
     */
    static serializeValue(value) {
        if (value === null || value === undefined) {
            return value;
        }
        // 基本类型
        const type = typeof value;
        if (type === 'string' || type === 'number' || type === 'boolean') {
            return value;
        }
        // Date
        if (value instanceof Date) {
            return { __type: 'Date', value: value.toISOString() };
        }
        // Map
        if (value instanceof Map) {
            return { __type: 'Map', value: Array.from(value.entries()) };
        }
        // Set
        if (value instanceof Set) {
            return { __type: 'Set', value: Array.from(value) };
        }
        // 数组
        if (Array.isArray(value)) {
            return value.map((item) => this.serializeValue(item));
        }
        // 普通对象
        if (type === 'object') {
            const result = {};
            for (const key in value) {
                if (value.hasOwnProperty(key)) {
                    result[key] = this.serializeValue(value[key]);
                }
            }
            return result;
        }
        // 其他类型不序列化
        return undefined;
    }
    /**
     * 反序列化单个值
     */
    static deserializeValue(value) {
        if (value === null || value === undefined) {
            return value;
        }
        // 基本类型
        const type = typeof value;
        if (type === 'string' || type === 'number' || type === 'boolean') {
            return value;
        }
        // 处理特殊类型标记
        if (type === 'object' && value.__type) {
            switch (value.__type) {
                case 'Date':
                    return new Date(value.value);
                case 'Map':
                    return new Map(value.value);
                case 'Set':
                    return new Set(value.value);
            }
        }
        // 数组
        if (Array.isArray(value)) {
            return value.map((item) => this.deserializeValue(item));
        }
        // 普通对象
        if (type === 'object') {
            const result = {};
            for (const key in value) {
                if (value.hasOwnProperty(key)) {
                    result[key] = this.deserializeValue(value[key]);
                }
            }
            return result;
        }
        return value;
    }
    /**
     * 过滤要序列化的实体和组件
     */
    static filterEntities(scene, options) {
        const entities = Array.from(scene.entities.buffer);
        // 如果指定了组件类型过滤
        if (options.components && options.components.length > 0) {
            const componentTypeSet = new Set(options.components);
            // 只返回拥有指定组件的实体
            return entities.filter((entity) => {
                return Array.from(entity.components).some((component) => componentTypeSet.has(component.constructor));
            });
        }
        return entities;
    }
    /**
     * 构建组件类型注册表
     */
    static buildComponentTypeRegistry(entities) {
        const registry = new Map();
        for (const entity of entities) {
            for (const component of entity.components) {
                const componentType = component.constructor;
                const typeName = getComponentTypeName(componentType);
                const metadata = getSerializationMetadata(component);
                if (metadata && !registry.has(typeName)) {
                    registry.set(typeName, metadata.options.version);
                }
            }
        }
        return Array.from(registry.entries()).map(([typeName, version]) => ({
            typeName,
            version
        }));
    }
    /**
     * 获取全局组件注册表
     *
     * 从所有已注册的组件类型构建注册表
     */
    static getGlobalComponentRegistry() {
        return ComponentRegistry.getAllComponentNames();
    }
    /**
     * 验证保存数据的有效性
     *
     * @param saveData 序列化的数据
     * @returns 验证结果
     */
    static validate(saveData) {
        const errors = [];
        try {
            const data = JSON.parse(saveData);
            if (!data.version) {
                errors.push('Missing version field');
            }
            if (!data.entities || !Array.isArray(data.entities)) {
                errors.push('Missing or invalid entities field');
            }
            if (!data.componentTypeRegistry || !Array.isArray(data.componentTypeRegistry)) {
                errors.push('Missing or invalid componentTypeRegistry field');
            }
            return {
                valid: errors.length === 0,
                version: data.version,
                ...(errors.length > 0 && { errors })
            };
        }
        catch (error) {
            return {
                valid: false,
                errors: [`JSON parse error: ${error}`]
            };
        }
    }
    /**
     * 获取保存数据的信息（不完全反序列化）
     *
     * @param saveData 序列化的数据
     * @returns 保存数据的元信息
     */
    static getInfo(saveData) {
        try {
            const data = JSON.parse(saveData);
            return {
                name: data.name,
                version: data.version,
                ...(data.timestamp !== undefined && { timestamp: data.timestamp }),
                entityCount: data.metadata?.entityCount || data.entities.length,
                componentTypeCount: data.componentTypeRegistry.length
            };
        }
        catch (error) {
            return null;
        }
    }
}
/**
 * 当前序列化版本
 */
SceneSerializer.SERIALIZATION_VERSION = 1;
//# sourceMappingURL=SceneSerializer.js.map